// followUpPick.ts — 2026-10-09 (Manak, won wrap-up v2).
// The web Mark-follow-up-done box sends the rep to the REAL Sales Orders /
// Proposals lists or the REAL New Sales Order form (?leadId=&pick=followup&ret=)
// and gets the picked sale / proposal back here. sessionStorage, keyed by lead,
// read once. Nothing is written to any order or proposal by this hand-off.
import type { DoneLink } from './followUpDoneApi';

const PICK = 'aura.followUpPick';
const DRAFT = 'aura.followUpDraft';
const DEFAULT_RET = '/follow-ups';

const get = (k: string): any => { try { return JSON.parse(sessionStorage.getItem(k) ?? 'null'); } catch { return null; } };
const set = (k: string, v: unknown) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } };
const drop = (k: string) => { try { sessionStorage.removeItem(k); } catch { /* private mode */ } };

export function savePick(leadId: string, link: DoneLink): void { set(PICK, { leadId, link }); }

export function takePick(leadId: string): DoneLink | null {
  const v = get(PICK);
  if (!v || v.leadId !== leadId) return null;
  drop(PICK);
  return v.link as DoneLink;
}

/** What the rep had typed / picked in the box before leaving to pick. */
export interface DoneDraft { code: string | null; notes: string; connected: boolean; selectedCallId: string | null }

/** Who the box was open for — the page reopens the box with it on return. */
export interface DoneReopenTarget { name: string; leadId: string; followUpRowId?: string | null }

export function saveDraft(leadId: string, d: DoneDraft, target?: DoneReopenTarget): void { set(DRAFT, { leadId, d, target: target ?? null }); }

/** On return (`#done=<leadId>` in the URL): the box target from the saved
 * draft, without using the draft up (the box reads it next). */
export function reopenTarget(hash: string): DoneReopenTarget | null {
  const m = /^#done=(.+)$/.exec(hash || '');
  if (!m) return null;
  const leadId = decodeURIComponent(m[1]);
  const v = get(DRAFT);
  return v && v.leadId === leadId && v.target ? (v.target as DoneReopenTarget) : null;
}

export function takeDraft(leadId: string): DoneDraft | null {
  const v = get(DRAFT);
  if (!v || v.leadId !== leadId) return null;
  drop(DRAFT);
  return v.d as DoneDraft;
}

export type PickKind = 'sale' | 'proposal' | 'new-sale';

export function pickHref(kind: PickKind, leadId: string, ret: string): string {
  const base = kind === 'sale' ? '/sales-orders' : kind === 'proposal' ? '/proposals' : '/sales-orders/new';
  return `${base}?leadId=${encodeURIComponent(leadId)}&pick=followup&ret=${encodeURIComponent(ret)}`;
}

/** 🔒 Only an in-app path is followed back ("/x", never "//host" or "https:"). */
function safeRet(r: string | null): string {
  return r && r.startsWith('/') && !r.startsWith('//') ? r : DEFAULT_RET;
}

export function isPickMode(sp: URLSearchParams): { leadId: string; ret: string } | null {
  const leadId = sp.get('leadId');
  return sp.get('pick') === 'followup' && leadId ? { leadId, ret: safeRet(sp.get('ret')) } : null;
}

/** Orders that cannot be tagged (server answers 409 for these). */
export function canTagOrder(o: { status?: string | null; gateStatus?: string | null }): boolean {
  return o.status !== 'cancelled' && o.status !== 'deleted' && o.gateStatus !== 'PENDING_APPROVAL';
}
